import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Calculator,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Check,
  TrendingDown,
  CalendarDays,
} from 'lucide-react';
import { BRAND } from '@/config/brand';
import { PACKAGES } from '@/config/packages';
import { PublicLayout } from '@/components/public';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NAV_PROPS = {
  links: [],
  primaryCta: { href: '/', label: 'Become a partner', testId: 'tools-nav-join-cta' },
};

const FOOTER_PROPS = {
  tagline:
    'A Canadian-owned delivery platform that keeps more of every sale with the local businesses that feed our communities.',
  columns: [
    {
      title: 'For Restaurants',
      links: [
        { href: '/', label: 'Why Hungry Canadian', testId: 'tools-footer-why' },
        { href: '/tools', label: 'Tools', testId: 'tools-footer-tools' },
      ],
    },
    {
      title: 'Resources',
      links: [
        { href: 'https://help.hungrycanadian.com/', label: 'Help center', external: true, testId: 'tools-footer-help' },
        { href: `mailto:${BRAND.merchantsEmail}`, label: 'Contact merchants', external: true, testId: 'tools-footer-contact' },
      ],
    },
    {
      title: 'Company',
      links: [
        { href: BRAND.website, label: 'Main site', external: true, testId: 'tools-footer-main' },
        { href: `mailto:${BRAND.merchantsEmail}`, label: 'Contact merchants', external: true, testId: 'tools-footer-contact-2' },
      ],
    },
  ],
  legal: [
    { href: 'https://help.hungrycanadian.com/tos/', label: 'Terms of Service', external: true, testId: 'tools-footer-tos' },
    { href: 'https://help.hungrycanadian.com/privacypolicy/', label: 'Privacy Policy', external: true, testId: 'tools-footer-privacy' },
  ],
};

const WEEKS_PER_MONTH = 4.33;
const CALENDAR_LINK = 'https://calendar.app.google/JSXWf5GuSYWZbmnW8';

const PLANS = PACKAGES.map((p) => ({
  id: p.id,
  name: p.name,
  price: p.priceLabel,
  sub: p.sub,
  fee: p.monthlyFee,
  badge: p.badge,
  setup: p.setup,
  features: p.highlights,
}));

const CAD = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 });
const money = (v) => CAD.format(v);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fade = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
};

export default function PublicTools() {
  return (
    <PublicLayout navProps={NAV_PROPS} footerProps={FOOTER_PROPS}>
      <section className="py-10 md:py-14 px-4 sm:px-6 bg-card border-b border-border">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <span aria-hidden className="h-4 w-1 bg-primary" />
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-primary">
              Merchant tools
            </p>
          </div>
          <h1 className="text-3xl md:text-5xl font-heading font-black tracking-tight text-foreground leading-[1.05]">
            Merchant profit calculator
          </h1>
          <p className="mt-3 text-muted-foreground text-sm md:text-base leading-relaxed">
            See what commission is really costing you — then how a flat monthly
            fee changes the math.
          </p>
        </div>
      </section>

      <section className="py-8 md:py-12 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <ProfitCalculatorWizard />
        </div>
      </section>
    </PublicLayout>
  );
}

function ProfitCalculatorWizard() {
  const [step, setStep] = useState(1);
  const [aov, setAov] = useState(35);
  const [orders, setOrders] = useState(50);
  const [commission, setCommission] = useState(30);
  const [restaurantName, setRestaurantName] = useState('');
  const [contact, setContact] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('pro');
  const [leadId, setLeadId] = useState(null);

  const monthlyLoss = useMemo(
    () => aov * orders * WEEKS_PER_MONTH * (commission / 100),
    [aov, orders, commission]
  );

  const plan = PLANS.find((p) => p.id === selectedPlan) || PLANS[0];
  const savings = Math.max(0, monthlyLoss - plan.fee);

  const submitLead = async (e) => {
    e.preventDefault();
    const c = contact.trim();
    if (!restaurantName.trim() || !c) {
      toast.error('Please enter your restaurant name and contact.');
      return;
    }
    const isEmail = c.includes('@');
    const phoneDigits = c.replace(/\D/g, '');
    const valid = isEmail ? EMAIL_RE.test(c) : phoneDigits.length >= 10 && phoneDigits.length <= 15;
    if (!valid) {
      toast.error('Please enter a valid email address or phone number.');
      return;
    }
    if (!consent) {
      toast.error('Please check the consent box so we can share your results.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/leads/calculator`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          restaurant_name: restaurantName,
          contact: c,
          consent,
          avg_order_value: aov,
          orders_per_week: orders,
          commission_percent: commission,
          monthly_loss: Math.round(monthlyLoss),
          likely_plan: null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Something went wrong. Please try again.');
      }
      const json = await res.json().catch(() => ({}));
      if (typeof window !== 'undefined' && typeof window.gtag_report_conversion === 'function') {
        window.gtag_report_conversion();
      }
      setLeadId(json.lead_id || null);
      setSelectedPlan('pro');
      setStep(4);
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectPlan = async () => {
    if (leadId) {
      try {
        await fetch(`${API_URL}/api/leads/${leadId}/select-plan`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ plan: plan.name }),
        });
      } catch (err) {
        console.error('select-plan request failed:', err);
        /* non-blocking — lead is already captured */
      }
    }
    setStep(5);
  };

  return (
    <div
      data-testid="profit-calculator"
      className="relative bg-card border border-border shadow-[0_18px_50px_-24px_rgba(0,0,0,0.6)]"
    >
      {/* Neutral header with a single small red accent */}
      <div className="bg-secondary border-b border-border px-6 md:px-8 py-5 flex items-center gap-3">
        <div className="p-2 bg-primary/10 text-primary">
          <Calculator className="w-5 h-5" />
        </div>
        <div>
          <p className="font-heading font-black tracking-tight text-lg text-foreground">Profit calculator</p>
          <p className="text-muted-foreground text-xs">{step >= 5 ? 'All done' : `Step ${step > 3 ? 3 : step} of 3`}</p>
        </div>
      </div>

      {/* Neutral progress bar */}
      <div className="h-1 w-full bg-border">
        <div
          className="h-full bg-foreground/40 transition-all duration-500"
          style={{ width: `${(Math.min(step, 3) / 3) * 100}%` }}
        />
      </div>

      <div className="p-5 md:p-7">
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div key="s1" {...fade} data-testid="calc-step-1">
              <h2 className="text-2xl md:text-3xl font-heading font-black tracking-tight text-foreground">
                Let&apos;s look at your numbers.
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Drag the sliders to match your delivery business.
              </p>

              <SliderRow label="Average order value" value={aov} display={money(aov)} min={10} max={150} step={1} onChange={setAov} testId="calc-aov" />
              <SliderRow label="Delivery orders / week" value={orders} display={`${orders}`} min={5} max={500} step={5} onChange={setOrders} testId="calc-orders" />
              <SliderRow label="Current commission rate" value={commission} display={`${commission}%`} min={10} max={40} step={1} onChange={setCommission} testId="calc-commission" />

              <Button
                onClick={() => setStep(2)}
                data-testid="calc-step1-next"
                className="mt-8 w-full h-12 text-base font-semibold rounded-full bg-primary hover:bg-primary-hover text-primary-foreground"
              >
                Calculate My True Cost <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="s2" {...fade} data-testid="calc-step-2" className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 text-primary mb-4 mx-auto">
                <TrendingDown className="w-6 h-6" />
              </div>
              <p className="text-sm text-muted-foreground uppercase tracking-[0.15em]">
                You are currently losing approximately
              </p>
              <p className="mt-2 text-5xl md:text-6xl font-heading font-black tracking-tight text-primary" data-testid="calc-monthly-loss">
                {money(monthlyLoss)}
              </p>
              <p className="mt-1 text-muted-foreground">per month</p>
              <p className="mt-4 text-sm text-muted-foreground max-w-md mx-auto">
                Based on a {commission}% commission on {orders} orders/week at {money(aov)} average order value.
              </p>

              <Button
                onClick={() => setStep(3)}
                data-testid="calc-step2-next"
                className="mt-8 w-full h-12 text-base font-semibold rounded-full bg-primary hover:bg-primary-hover text-primary-foreground"
              >
                See My Hungry Canadian Savings <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <button
                type="button"
                onClick={() => setStep(1)}
                data-testid="calc-step2-back"
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Wait, let me adjust my numbers
              </button>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="s3" {...fade} data-testid="calc-step-3">
              <h2 className="text-2xl md:text-3xl font-heading font-black tracking-tight text-foreground">
                Unlock your flat-rate savings.
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Tell us where to send your results and our team will follow up.
              </p>

              <form onSubmit={submitLead} noValidate className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="calc-rname" className="text-sm text-foreground font-medium mb-1.5 block">
                    Restaurant name
                  </Label>
                  <Input
                    id="calc-rname"
                    data-testid="calc-restaurant-name"
                    value={restaurantName}
                    onChange={(e) => setRestaurantName(e.target.value)}
                    placeholder="Your restaurant"
                    className="h-12 bg-background border-border text-foreground rounded-none"
                    autoFocus
                  />
                </div>
                <div>
                  <Label htmlFor="calc-contact" className="text-sm text-foreground font-medium mb-1.5 block">
                    Mobile number or email
                  </Label>
                  <Input
                    id="calc-contact"
                    data-testid="calc-contact"
                    value={contact}
                    onChange={(e) => setContact(e.target.value)}
                    placeholder="you@restaurant.com or (403) 555-1234"
                    className="h-12 bg-background border-border text-foreground rounded-none"
                  />
                </div>

                <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="calc-consent-label">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    data-testid="calc-consent"
                    className="mt-1 h-4 w-4 shrink-0 accent-[hsl(var(--primary))]"
                  />
                  <span className="text-xs text-muted-foreground leading-relaxed">
                    I agree that Hungry Canadian may contact me by email, phone, or
                    text about my results and its services.
                  </span>
                </label>

                <Button
                  type="submit"
                  data-testid="calc-step3-submit"
                  disabled={submitting}
                  className="w-full h-12 text-base font-semibold rounded-full bg-primary hover:bg-primary-hover text-primary-foreground"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Calculating…</>
                  ) : (
                    <>Reveal My Savings <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>

                <p className="text-[11px] text-muted-foreground leading-relaxed" data-testid="calc-consent-footnote">
                  By submitting, you provide express consent under Canada&apos;s
                  Anti-Spam Legislation (CASL) for Hungry Canadian to contact you
                  about your results and services. You can withdraw consent anytime
                  via the unsubscribe link in our emails or by contacting {BRAND.merchantsEmail}. Hungry Canadian, Alberta, Canada.
                </p>
              </form>
            </motion.div>
          )}

          {step === 4 && (
            <motion.div key="s4" {...fade} data-testid="calc-step-4">
              {/* Neutral savings banner, red only on the number */}
              <div className="bg-secondary border border-border p-5 text-center mb-5">
                <p className="text-muted-foreground text-xs uppercase tracking-[0.15em]">
                  Your potential monthly savings
                </p>
                <p className="mt-1 text-4xl md:text-5xl font-heading font-black tracking-tight text-primary" data-testid="calc-savings-amount">
                  +{money(savings)}
                </p>
                <p className="mt-1 text-muted-foreground text-sm">
                  based on the {plan.name} plan
                </p>
              </div>

              <p className="text-sm text-muted-foreground">
                Select your package to compare savings.
              </p>
              <p className="mt-1 mb-5 text-xs text-muted-foreground/80" data-testid="calc-processing-footnote">
                Credit card processing fees apply per order. Taxes (e.g., GST/PST) are added where applicable.
              </p>

              {/* Segmented package selector */}
              <div className="flex items-center gap-1 p-1 bg-secondary rounded-full mb-7" data-testid="calc-plan-tabs">
                {PLANS.map((p) => {
                  const active = selectedPlan === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSelectedPlan(p.id)}
                      data-testid={`calc-plan-${p.id}`}
                      className={`flex-1 px-2 py-2 rounded-full text-xs sm:text-sm font-semibold transition-colors ${
                        active ? 'bg-foreground text-background shadow' : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {p.name}
                    </button>
                  );
                })}
              </div>

              {/* Selected plan detail */}
              <div className="relative border-2 border-primary/60 p-6 pt-8 text-center" data-testid="calc-plan-detail">
                {plan.badge && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap bg-primary text-primary-foreground text-[10px] font-bold px-3 py-1 uppercase tracking-wide">
                    {plan.badge}
                  </span>
                )}
                <p className="font-heading font-bold text-lg text-foreground">{plan.name}</p>
                <p className="mt-1 font-heading font-black text-4xl md:text-5xl text-foreground leading-none">
                  {plan.price}
                  <span className="text-base text-muted-foreground font-medium">{plan.sub}</span>
                </p>
                <p className="mt-1 text-xs text-muted-foreground">{plan.setup}</p>
                <p className="mt-2 text-sm font-semibold text-primary" data-testid="calc-plan-save">
                  You save {money(savings)}/mo
                </p>
                <ul className="mt-5 space-y-2 text-left max-w-xs mx-auto">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Check className="w-4 h-4 text-primary shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>

              <button
                type="button"
                onClick={selectPlan}
                data-testid="calc-select-plan"
                className="mt-5 block w-full py-4 rounded-full bg-primary text-primary-foreground text-center font-semibold hover:bg-primary-hover transition-colors"
              >
                Select This Plan →
              </button>
            </motion.div>
          )}

          {step === 5 && (
            <motion.div key="s5" {...fade} data-testid="calc-step-5" className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-primary/10 text-primary mb-4 mx-auto">
                <Check className="w-7 h-7" />
              </div>
              <h2 className="text-2xl md:text-3xl font-heading font-black tracking-tight text-foreground">
                You&apos;re all set{restaurantName ? `, ${restaurantName}` : ''}.
              </h2>
              <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed max-w-md mx-auto">
                Thanks for your interest in{' '}
                <span className="text-foreground font-semibold">{plan.name}</span>. We&apos;ve
                received your details and a member of our team will reach out within 1 business day to
                walk through your numbers and tailor a plan — including options not shown here — to your
                business.
              </p>

              <div className="mt-6 bg-secondary border border-border p-4 text-left max-w-sm mx-auto" data-testid="calc-recap">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Plan of interest</span>
                  <span className="text-foreground font-semibold">{plan.name}</span>
                </div>
                <div className="flex justify-between text-sm mt-1.5">
                  <span className="text-muted-foreground">Est. monthly savings</span>
                  <span className="text-primary font-semibold">+{money(savings)}</span>
                </div>
              </div>

              <a
                href={CALENDAR_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="calc-book-meeting"
                className="mt-6 inline-flex items-center justify-center gap-2 w-full py-4 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-primary-hover transition-colors"
              >
                <CalendarDays className="w-5 h-5" /> Book a meeting
              </a>
              <div className="mt-4">
                <a
                  href="/"
                  data-testid="calc-back-home"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Back to Hungry Canadian →
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* Isolated neutral slider (keeps red out of the controls) */
function CalcSlider({ value, min, max, step, onValueChange, testId }) {
  return (
    <SliderPrimitive.Root
      className="relative flex w-full touch-none select-none items-center"
      value={value}
      min={min}
      max={max}
      step={step}
      onValueChange={onValueChange}
      data-testid={testId}
    >
      <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-border">
        <SliderPrimitive.Range className="absolute h-full bg-foreground/70" />
      </SliderPrimitive.Track>
      <SliderPrimitive.Thumb className="block h-4 w-4 rounded-full border-2 border-foreground bg-background shadow transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
    </SliderPrimitive.Root>
  );
}

function SliderRow({ label, value, display, min, max, step, onChange, testId }) {
  return (
    <div className="mt-6">
      <div className="flex items-baseline justify-between mb-3">
        <Label className="text-sm text-foreground font-medium">{label}</Label>
        <span className="font-heading font-black text-xl text-foreground" data-testid={`${testId}-value`}>
          {display}
        </span>
      </div>
      <CalcSlider value={[value]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0])} testId={`${testId}-slider`} />
    </div>
  );
}
